package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.Report
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.AppSession
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UsageSyncManager
import co.uk.sacursoftware.ssinventory.util.UserRole
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SettingsScreen(
    onBack: () -> Unit,
    onProfileClick: () -> Unit,
    onFilesClick: () -> Unit
) {
    val userRole = LocalUserRole.current
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val syncManager = remember { UsageSyncManager(repository) }
    
    val syncStatus by syncManager.syncStatus.collectAsState()
    
    var showReportDialog by remember { mutableStateOf(false) }
    var reports by remember { mutableStateOf<List<Report>>(emptyList()) }
    var isLoadingReports by remember { mutableStateOf(false) }
    val userProfile = AppSession.userProfile

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Admin") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .padding(padding)
                .fillMaxSize()
                .padding(16.dp)
        ) {
            Text(
                "Data Management",
                style = MaterialTheme.typography.titleMedium,
                color = MaterialTheme.colorScheme.secondary,
                modifier = Modifier.padding(bottom = 8.dp)
            )

            if (userRole == UserRole.ADMIN) {
                val profileButtonText = if (userProfile.isNullOrEmpty()) "Create Profile" else "See Profile"
                Button(
                    onClick = onProfileClick,
                    modifier = Modifier.fillMaxWidth().height(60.dp),
                    shape = RectangleShape,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary
                    )
                ) {
                    Text(profileButtonText, style = MaterialTheme.typography.titleMedium)
                }
                
                Spacer(modifier = Modifier.height(16.dp))

                Button(
                    onClick = onFilesClick,
                    modifier = Modifier.fillMaxWidth().height(60.dp),
                    shape = RectangleShape,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary
                    )
                ) {
                    Text("Files", style = MaterialTheme.typography.titleMedium)
                }
                
                Spacer(modifier = Modifier.height(16.dp))

                Button(
                    onClick = {
                        scope.launch {
                            isLoadingReports = true
                            showReportDialog = true
                            repository.getReports().onSuccess {
                                reports = it.sortedByDescending { r -> r.startDate }
                                isLoadingReports = false
                            }.onFailure {
                                isLoadingReports = false
                                snackbarHostState.showSnackbar("Failed to load reports")
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(60.dp),
                    shape = RectangleShape,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.secondary
                    )
                ) {
                    Text("Sync Weekly Usage", style = MaterialTheme.typography.titleMedium)
                }
            } else {
                Text(
                    "Standard users do not have access to data management tools.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.outline
                )
            }

        }

        if (showReportDialog) {
            ReportSelectionDialog(
                reports = reports,
                isLoading = isLoadingReports,
                onDismiss = { showReportDialog = false },
                onSync = { selected ->
                    showReportDialog = false
                    scope.launch {
                        syncManager.syncUsage(selected).fold(
                            onSuccess = { count ->
                                snackbarHostState.showSnackbar("Successfully updated $count items")
                            },
                            onFailure = {
                                snackbarHostState.showSnackbar("Sync failed: ${it.message}")
                            }
                        )
                    }
                }
            )
        }

        syncStatus?.let { status ->
            AlertDialog(
                onDismissRequest = {},
                title = { Text("Syncing Usage") },
                text = {
                    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.fillMaxWidth()) {
                        BrandLoadingSpinner()
                        Spacer(modifier = Modifier.height(16.dp))
                        Text(status)
                    }
                },
                confirmButton = {}
            )
        }
    }
}

@Composable
fun ReportSelectionDialog(
    reports: List<Report>,
    isLoading: Boolean,
    onDismiss: () -> Unit,
    onSync: (List<Report>) -> Unit
) {
    val selectedReports = remember { mutableStateListOf<Report>() }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Select Reports for Sync") },
        text = {
            Box(modifier = Modifier.heightIn(max = 400.dp)) {
                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().height(200.dp), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner(Modifier.fillMaxSize())
                    }
                } else {
                    LazyColumn {
                        items(reports) { report ->
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        if (selectedReports.contains(report)) {
                                            selectedReports.remove(report)
                                        } else {
                                            selectedReports.add(report)
                                        }
                                    }
                                    .padding(vertical = 12.dp),
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                Checkbox(
                                    checked = selectedReports.contains(report),
                                    onCheckedChange = null
                                )
                                Spacer(modifier = Modifier.width(8.dp))
                                Text(
                                    "${DateTimeUtils.formatIsoDisplayDate(report.startDate)} - ${DateTimeUtils.formatIsoDisplayDate(report.endDate)}",
                                    style = MaterialTheme.typography.bodyLarge
                                )
                            }
                            HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                        }
                    }
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { onSync(selectedReports.toList()) },
                enabled = selectedReports.isNotEmpty() && !isLoading,
                shape = MaterialTheme.shapes.medium
            ) {
                Text("Sync Now")
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("Cancel")
            }
        }
    )
}
